'use client';
import React, { useState } from 'react';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';

type AdminSection = 'overview' | 'users' | 'queues' | 'ai-governance' | 'audit' | 'integration';

const roles = [
  { name: 'Customer Service Agent', users: 245, permissions: ['View Interactions', 'Handle Interactions', 'View Cases', 'Create Cases', 'View Knowledge'] },
  { name: 'Senior Agent', users: 42, permissions: ['All Agent', 'Escalate Cases', 'Edit Cases', 'View Analytics'] },
  { name: 'Supervisor', users: 18, permissions: ['All Senior Agent', 'View All Agents', 'Live Monitoring', 'Override SLA', 'View Reports'] },
  { name: 'Knowledge Manager', users: 8, permissions: ['Manage Knowledge', 'Publish Articles', 'View Analytics', 'Agency Coordination'] },
  { name: 'System Administrator', users: 4, permissions: ['Full Access', 'User Management', 'System Configuration', 'AI Governance', 'Audit Logs'] },
  { name: 'Agency Officer', users: 156, permissions: ['View Assigned Cases', 'Update Case Status', 'View Agency Knowledge'] },
  { name: 'Auditor', users: 3, permissions: ['Read-Only All', 'Export Audit Logs', 'View AI Decisions'] },
];

const aiControls = [
  { label: 'Human Review Required', desc: 'All AI recommendations require agent approval before action', enabled: true, critical: true },
  { label: 'Knowledge Grounding', desc: 'AI responses must be grounded in approved knowledge sources', enabled: true, critical: true },
  { label: 'Sentiment Analysis', desc: 'Real-time sentiment detection across all channels', enabled: true, critical: false },
  { label: 'Interaction Summarisation', desc: 'AI generates post-interaction summaries for agent review', enabled: true, critical: false },
  { label: 'AI Suggested Responses', desc: 'AI drafts suggested responses for agent consideration', enabled: true, critical: false },
  { label: 'Sensitive Data Masking', desc: 'Automatically mask NRIC, phone numbers in AI outputs', enabled: true, critical: true },
  { label: 'Audit Logging', desc: 'All AI decisions and agent actions are logged', enabled: true, critical: true },
  { label: 'Automatic Case Closure', desc: 'Allow AI to automatically close resolved cases', enabled: false, critical: true },
  { label: 'Automatic Case Creation', desc: 'Allow AI to create cases without agent review', enabled: false, critical: true },
];

const auditLog = [
  { time: '14:32', interaction: 'INT-14892', agent: 'Nur Aisyah', action: 'Suggested Case Category', confidence: '94%', decision: 'Accepted' },
  { time: '14:33', interaction: 'INT-14892', agent: 'Nur Aisyah', action: 'Generated Interaction Summary', confidence: '91%', decision: 'Edited' },
  { time: '14:28', interaction: 'INT-14891', agent: 'Nur Aisyah', action: 'Sentiment Analysis — Frustrated', confidence: '89%', decision: 'Noted' },
  { time: '14:25', interaction: 'INT-14890', agent: 'Nur Aisyah', action: 'Knowledge Article Recommended', confidence: '96%', decision: 'Opened' },
  { time: '14:15', interaction: 'INT-14888', agent: 'Hafizuddin', action: 'Suggested Response (BM)', confidence: '87%', decision: 'Used' },
  { time: '14:10', interaction: 'INT-14887', agent: 'Siti Rohaida', action: 'Intent Detection — MyDigital ID', confidence: '93%', decision: 'Accepted' },
  { time: '14:05', interaction: 'INT-14886', agent: 'Ahmad Shahril', action: 'Case Classification', confidence: '88%', decision: 'Edited' },
  { time: '13:58', interaction: 'INT-14885', agent: 'Nurul Faizah', action: 'Duplicate Case Detection', confidence: '76%', decision: 'Reviewed' },
];

const integrations = [
  { name: 'Mitel Communication Platform', type: 'Voice / Telephony', status: 'Active', env: 'Production', lastSync: '14:35' },
  { name: 'Mitel MIR Recording', type: 'Interaction Recording', status: 'Active', env: 'Production', lastSync: '14:35' },
  { name: 'MyDigital ID SSO', type: 'Authentication', status: 'Demo / Simulated', env: 'Prototype', lastSync: '14:32' },
  { name: 'WhatsApp Business API', type: 'Messaging Channel', status: 'Active', env: 'Production', lastSync: '14:34' },
  { name: 'GovChat Platform', type: 'Government Channel', status: 'Active', env: 'Production', lastSync: '14:33' },
  { name: 'Jabatan Imigresen API', type: 'Agency Integration', status: 'Active', env: 'Production', lastSync: '14:30' },
  { name: 'MOH MySejahtera API', type: 'Agency Integration', status: 'Active', env: 'Production', lastSync: '14:28' },
  { name: 'CX AI Assist Engine', type: 'AI / ML Service', status: 'Active', env: 'Production', lastSync: '14:35' },
];

export default function AdministrationContent() {
  const [section, setSection] = useState<AdminSection>('overview');
  const [aiSettings, setAiSettings] = useState(aiControls.map(c => ({ ...c })));
  const [confidenceThreshold, setConfidenceThreshold] = useState(85);

  const toggleAI = (index: number) => {
    if (aiSettings[index].critical && aiSettings[index].enabled) {
      toast.warning('This is a critical AI governance setting. Supervisor approval required to disable.');
      return;
    }
    setAiSettings(prev => prev.map((s, i) => i === index ? { ...s, enabled: !s.enabled } : s));
    toast.success(`${aiSettings[index].label} ${aiSettings[index].enabled ? 'disabled' : 'enabled'}`);
  };

  const navItems: { key: AdminSection; label: string; icon: string }[] = [
    { key: 'overview', label: 'Overview', icon: 'HomeIcon' },
    { key: 'users', label: 'Users & Roles', icon: 'UsersIcon' },
    { key: 'queues', label: 'Queues & Routing', icon: 'QueueListIcon' },
    { key: 'ai-governance', label: 'AI Governance', icon: 'SparklesIcon' },
    { key: 'audit', label: 'Audit Logs', icon: 'ClipboardDocumentListIcon' },
    { key: 'integration', label: 'Integration Status', icon: 'LinkIcon' },
  ];

  return (
    <div className="max-w-screen-2xl mx-auto px-5 py-4 page-transition">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-[18px] font-700 text-foreground">Administration</h1>
          <p className="text-[12px] text-muted-foreground mt-0.5">System configuration, user management, and AI governance</p>
        </div>
      </div>

      <div className="flex gap-4">
        {/* Side nav */}
        <div className="w-48 flex-shrink-0">
          <div className="card-elevated p-2">
            {navItems.map(item => (
              <button
                key={item.key}
                onClick={() => setSection(item.key)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-[12px] font-600 transition-all mb-0.5 text-left ${section === item.key ? 'bg-primary text-white' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
              >
                <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={14} />
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {section === 'overview' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { label: 'Total Users', value: '476', icon: 'UsersIcon', color: 'text-blue-600', bg: 'bg-blue-50' },
                  { label: 'Active Agents', value: '287', icon: 'UserCircleIcon', color: 'text-green-600', bg: 'bg-green-50' },
                  { label: 'Active Queues', value: '7', icon: 'QueueListIcon', color: 'text-purple-600', bg: 'bg-purple-50' },
                  { label: 'Integrations', value: '8 / 8', icon: 'LinkIcon', color: 'text-teal-600', bg: 'bg-teal-50' },
                ].map(s => (
                  <div key={s.label} className="card-elevated p-4">
                    <div className={`p-2 rounded-xl ${s.bg} w-fit mb-3`}>
                      <Icon name={s.icon as Parameters<typeof Icon>[0]['name']} size={15} className={s.color} />
                    </div>
                    <div className="text-[22px] font-700 font-tabular text-foreground">{s.value}</div>
                    <div className="text-[11px] text-muted-foreground">{s.label}</div>
                  </div>
                ))}
              </div>
              <div className="card-elevated p-4">
                <h3 className="text-[13px] font-700 mb-3">Quick Actions</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { label: 'Add User', icon: 'UserPlusIcon' },
                    { label: 'Configure Queue', icon: 'AdjustmentsHorizontalIcon' },
                    { label: 'AI Governance', icon: 'SparklesIcon' },
                    { label: 'View Audit Log', icon: 'ClipboardDocumentListIcon' },
                  ].map(a => (
                    <button key={a.label} onClick={() => toast.info(a.label)} className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-border hover:bg-muted transition-colors text-[12px] font-600 text-foreground">
                      <Icon name={a.icon as Parameters<typeof Icon>[0]['name']} size={14} className="text-primary" />
                      {a.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {section === 'users' && (
            <div className="card-elevated overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <h2 className="text-[13px] font-700">Roles & Permissions</h2>
                <button onClick={() => toast.info('Add role')} className="btn-primary px-3 py-1.5 text-[12px] flex items-center gap-1.5">
                  <Icon name="PlusIcon" size={12} />
                  Add Role
                </button>
              </div>
              <div className="divide-y divide-border">
                {roles.map(role => (
                  <div key={role.name} className="px-4 py-3 hover:bg-muted/20 transition-colors">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="text-[13px] font-700 text-foreground">{role.name}</div>
                        <div className="text-[11px] text-muted-foreground">{role.users} users</div>
                      </div>
                      <button onClick={() => toast.info(`Editing ${role.name}`)} className="btn-ghost px-2 py-1 text-[11px] border border-border">Edit</button>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {role.permissions.map(p => (
                        <span key={p} className="text-[10px] bg-secondary text-primary px-2 py-0.5 rounded-md font-500 border border-blue-200">{p}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {section === 'ai-governance' && (
            <div className="space-y-4">
              <div className="ai-panel border rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Icon name="SparklesIcon" size={15} className="text-primary" />
                  <h2 className="text-[14px] font-700 text-primary">AI Governance Controls</h2>
                  <span className="ai-badge">AI</span>
                </div>
                <p className="text-[12px] text-muted-foreground mb-4">Configure how CX AI Assist operates within MyGCC. Critical settings require supervisor approval to change.</p>

                {/* Confidence threshold */}
                <div className="card-elevated p-4 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="text-[13px] font-700 text-foreground">Confidence Threshold</div>
                      <div className="text-[11px] text-muted-foreground">Minimum AI confidence required before showing recommendations</div>
                    </div>
                    <span className="text-[20px] font-700 text-primary font-tabular">{confidenceThreshold}%</span>
                  </div>
                  <input
                    type="range"
                    min={60}
                    max={99}
                    value={confidenceThreshold}
                    onChange={e => setConfidenceThreshold(Number(e.target.value))}
                    className="w-full accent-primary"
                  />
                  <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                    <span>60% (Permissive)</span>
                    <span>99% (Strict)</span>
                  </div>
                </div>

                <div className="space-y-2">
                  {aiSettings.map((control, i) => (
                    <div key={control.label} className={`flex items-start justify-between p-3 rounded-xl border ${control.critical ? 'border-amber-200 bg-amber-50/30' : 'border-border bg-card'}`}>
                      <div className="flex-1 min-w-0 mr-4">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-[12px] font-700 text-foreground">{control.label}</span>
                          {control.critical && <span className="text-[9px] bg-amber-100 text-amber-700 border border-amber-300 px-1.5 py-0.5 rounded font-700">CRITICAL</span>}
                        </div>
                        <div className="text-[11px] text-muted-foreground">{control.desc}</div>
                      </div>
                      <button
                        onClick={() => toggleAI(i)}
                        className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${control.enabled ? 'bg-primary' : 'bg-muted'}`}
                      >
                        <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${control.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {section === 'audit' && (
            <div className="card-elevated overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <h2 className="text-[13px] font-700">AI Audit Log</h2>
                <button onClick={() => toast.info('Exporting audit log')} className="btn-ghost px-3 py-1.5 border border-border text-[12px] flex items-center gap-1.5">
                  <Icon name="ArrowDownTrayIcon" size={13} />
                  Export
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="bg-muted/40 border-b border-border">
                      {['Time', 'Interaction', 'Agent', 'AI Action', 'Confidence', 'Agent Decision'].map(h => (
                        <th key={h} className="text-left px-3 py-2.5 text-[10px] font-700 uppercase tracking-wide text-muted-foreground whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {auditLog.map((row, i) => (
                      <tr key={i} className="table-row-hover transition-colors">
                        <td className="px-3 py-2.5 font-tabular text-muted-foreground">{row.time}</td>
                        <td className="px-3 py-2.5 font-700 text-primary">{row.interaction}</td>
                        <td className="px-3 py-2.5 text-foreground">{row.agent}</td>
                        <td className="px-3 py-2.5 text-foreground">{row.action}</td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-1.5">
                            <div className="h-1.5 w-16 bg-muted rounded-full overflow-hidden">
                              <div className="h-full bg-primary rounded-full" style={{ width: row.confidence }} />
                            </div>
                            <span className="font-700 text-primary font-tabular">{row.confidence}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2.5">
                          <span className={`text-[10px] font-600 px-2 py-0.5 rounded-md border ${
                            row.decision === 'Accepted' || row.decision === 'Used' ? 'bg-green-50 text-green-700 border-green-200' :
                            row.decision === 'Edited'? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-50 text-slate-600 border-slate-200'
                          }`}>{row.decision}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {section === 'integration' && (
            <div className="card-elevated overflow-hidden">
              <div className="px-4 py-3 border-b border-border">
                <h2 className="text-[13px] font-700">Integration Status</h2>
                <p className="text-[11px] text-muted-foreground mt-0.5">All external system connections and their current status</p>
              </div>
              <div className="divide-y divide-border">
                {integrations.map(int => (
                  <div key={int.name} className="flex items-center justify-between px-4 py-3 hover:bg-muted/20 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${int.status === 'Active' ? 'bg-green-500' : 'bg-amber-500'}`} />
                      <div>
                        <div className="text-[13px] font-600 text-foreground">{int.name}</div>
                        <div className="text-[11px] text-muted-foreground">{int.type}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-right">
                      <div>
                        <div className={`text-[11px] font-700 ${int.status === 'Active' ? 'text-green-600' : 'text-amber-600'}`}>{int.status}</div>
                        <div className="text-[10px] text-muted-foreground">{int.env}</div>
                      </div>
                      <div className="text-[10px] text-muted-foreground">Last sync: {int.lastSync}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {section === 'queues' && (
            <div className="card-elevated overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <h2 className="text-[13px] font-700">Queue Configuration</h2>
                <button onClick={() => toast.info('Add queue')} className="btn-primary px-3 py-1.5 text-[12px] flex items-center gap-1.5">
                  <Icon name="PlusIcon" size={12} />
                  Add Queue
                </button>
              </div>
              <div className="divide-y divide-border">
                {[
                  { name: 'MyGCC General', agents: 45, sla: '4h', channels: 'All', priority: 'Normal' },
                  { name: 'Immigration', agents: 32, sla: '3h', channels: 'Voice, WhatsApp, Email', priority: 'High' },
                  { name: 'Health Services', agents: 28, sla: '4h', channels: 'All', priority: 'Normal' },
                  { name: 'Transport', agents: 18, sla: '6h', channels: 'Voice, Email', priority: 'Normal' },
                  { name: 'Taxation', agents: 22, sla: '4h', channels: 'Voice, Email, Web Chat', priority: 'Normal' },
                  { name: 'Government Assistance', agents: 35, sla: '4h', channels: 'All', priority: 'High' },
                  { name: 'Digital Services', agents: 20, sla: '6h', channels: 'Chat, Email, GovChat', priority: 'Normal' },
                ].map(q => (
                  <div key={q.name} className="flex items-center justify-between px-4 py-3 hover:bg-muted/20 transition-colors">
                    <div>
                      <div className="text-[13px] font-600 text-foreground">{q.name}</div>
                      <div className="text-[11px] text-muted-foreground">{q.channels}</div>
                    </div>
                    <div className="flex items-center gap-6 text-[12px]">
                      <div className="text-center">
                        <div className="font-700 text-foreground">{q.agents}</div>
                        <div className="text-[10px] text-muted-foreground">Agents</div>
                      </div>
                      <div className="text-center">
                        <div className="font-700 text-foreground">{q.sla}</div>
                        <div className="text-[10px] text-muted-foreground">SLA</div>
                      </div>
                      <span className={`text-[10px] font-600 px-2 py-0.5 rounded-md border ${q.priority === 'High' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-muted text-muted-foreground border-border'}`}>{q.priority}</span>
                      <button onClick={() => toast.info(`Editing ${q.name}`)} className="btn-ghost px-2 py-1 text-[11px] border border-border">Edit</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
